import { useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';

/**
 * Admin tool: scan or type a license, optionally set a state code,
 * and create a ban. Used when the original guest record was
 * already anonymized (so banning via the guest edit page isn't
 * possible) but staff have the physical ID. The licenseID is
 * hashed server-side by IdHashService and never persisted on the
 * ban record — only the resulting idHash + last4 are stored.
 */
const BanFromScanPage = () => {
    const history = useHistory();
    const [licenseID, setLicenseID] = useState('');
    const [state, setState] = useState('');
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState<{ last4: string } | null>(null);
    const [err, setErr] = useState('');

    const submit = async () => {
        setErr('');
        if (!licenseID.trim() || !reason) return;
        setSubmitting(true);
        try {
            const resp = await axios.post('/api/bans/from-license', {
                licenseID: licenseID.trim(),
                reason,
                notes: notes.trim(),
                issuingStateOrCountry: state.trim().toUpperCase(),
            });
            setDone({ last4: resp.data.last4 || '----' });
        } catch (e: any) {
            setErr(
                e?.response?.data?.message ||
                    e.message ||
                    'Ban creation failed',
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div style={{ padding: 24, color: '#fff', maxWidth: 540 }}>
                <h1 style={{ color: '#ff5555' }}>✅ Ban created</h1>
                <p>
                    Ban active. The guest will be blocked at the kiosk on next scan.
                </p>
                <p style={{ fontFamily: 'monospace', color: '#aaa' }}>
                    Last 4: {done.last4}
                </p>
                <button
                    onClick={() => history.push('/admin/bans')}
                    style={{
                        background: '#3a1a1a',
                        color: '#fff',
                        border: '1px solid #ff5555',
                        padding: '10px 18px',
                        borderRadius: 6,
                        cursor: 'pointer',
                    }}
                >
                    View bans
                </button>
            </div>
        );
    }

    return (
        <div style={{ padding: 24, color: '#fff', maxWidth: 540 }}>
            <h1 style={{ color: '#ff5555', marginBottom: 18 }}>
                🚫 Ban from scan
            </h1>
            <p style={{ color: '#ccc', marginBottom: 24 }}>
                Scan or type the license number to ban. The license itself is
                NOT stored — only its cryptographic hash, used to block
                future scans of the same ID.
            </p>

            <label style={lbl}>License # (scan or type)</label>
            <input
                type="text"
                autoFocus
                value={licenseID}
                onChange={(e) => setLicenseID(e.target.value)}
                placeholder="K00-70-0412 or scan output"
                style={inp}
            />

            <label style={lbl}>Issuing state (optional)</label>
            <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="KS"
                style={inp}
                maxLength={2}
            />

            <label style={lbl}>Reason</label>
            <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={inp}
            >
                <option value="">— select reason —</option>
                <option>Fighting / violence</option>
                <option>Drug activity</option>
                <option>Theft</option>
                <option>Underage / fake ID</option>
                <option>Aggressive behavior</option>
                <option>Repeated rule violations</option>
                <option>Other (see notes)</option>
            </select>

            <label style={lbl}>Notes</label>
            <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Detail / context"
                style={{ ...inp, fontFamily: 'inherit', resize: 'vertical' }}
            />

            {err && (
                <div
                    style={{
                        background: '#3a1a1a',
                        color: '#ff8888',
                        padding: 10,
                        borderRadius: 6,
                        marginTop: 12,
                    }}
                >
                    {err}
                </div>
            )}

            <div style={{ marginTop: 18, display: 'flex', gap: 8 }}>
                <button
                    onClick={() => history.push('/admin/bans')}
                    style={{
                        background: '#222',
                        color: '#aaa',
                        border: '1px solid #444',
                        padding: '10px 18px',
                        borderRadius: 6,
                        cursor: 'pointer',
                    }}
                >
                    Cancel
                </button>
                <button
                    onClick={submit}
                    disabled={!licenseID.trim() || !reason || submitting}
                    style={{
                        background:
                            licenseID.trim() && reason && !submitting
                                ? '#a31a1a'
                                : '#a31a1a66',
                        color: '#fff',
                        border: 'none',
                        padding: '10px 18px',
                        borderRadius: 6,
                        fontWeight: 700,
                        cursor:
                            licenseID.trim() && reason && !submitting
                                ? 'pointer'
                                : 'not-allowed',
                    }}
                >
                    {submitting ? 'Banning…' : '🚫 Confirm ban'}
                </button>
            </div>
        </div>
    );
};

const lbl: React.CSSProperties = {
    display: 'block',
    fontWeight: 600,
    marginTop: 14,
    marginBottom: 6,
    fontSize: '0.9rem',
};
const inp: React.CSSProperties = {
    width: '100%',
    background: '#111',
    color: '#fff',
    border: '1px solid #444',
    borderRadius: 6,
    padding: '10px',
    fontSize: 14,
    boxSizing: 'border-box',
};

export default BanFromScanPage;
